import React, { useEffect, useRef, useState } from 'react';
import { API } from '../api';
import './NotificationPanel.css';

const NotificationPanel = ({ onCampaignCreated }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [parseMode, setParseMode] = useState('Markdown');
  const [mediaType, setMediaType] = useState('text');
  const [mediaUrl, setMediaUrl] = useState('');
  const [buttons, setButtons] = useState([{ text: '', url: '' }]);
  const [campaignId, setCampaignId] = useState(null);
  const [progress, setProgress] = useState({ status: null, totalRecipients: 0, sentCount: 0, failedCount: 0, startedAt: null, finishedAt: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pollTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
      }
    };
  }, []);

  const updateButton = (index, field, value) => {
    setButtons((prev) => prev.map((button, idx) => idx === index ? { ...button, [field]: value } : button));
  };

  const addButton = () => {
    if (buttons.length >= 3) return;
    setButtons((prev) => [...prev, { text: '', url: '' }]);
  };

  const removeButton = (index) => {
    setButtons((prev) => prev.filter((_, idx) => idx !== index));
  };

  const buildPayload = () => ({
    title: title.trim() || undefined,
    message: message.trim(),
    parseMode,
    mediaType: mediaType === 'text' ? undefined : mediaType,
    mediaUrl: mediaType === 'text' ? undefined : mediaUrl.trim() || undefined,
    buttons: buttons
      .filter((button) => button.text.trim() && button.url.trim())
      .map((button) => ({ text: button.text.trim(), url: button.url.trim() })),
  });

  const handleApiError = (errorText) => {
    setError(errorText || 'Не удалось выполнить запрос.');
  };

  const handlePollStatus = async (id) => {
    if (!id) return;
    const result = await API.notifications.status(id);
    if (!result.success) {
      handleApiError(result.error || 'Не удалось получить статус рассылки.');
      return;
    }

    const campaign = result.data;
    setProgress({
      status: campaign.status,
      totalRecipients: campaign.totalRecipients || 0,
      sentCount: campaign.sentCount || 0,
      failedCount: campaign.failedCount || 0,
      startedAt: campaign.startedAt,
      finishedAt: campaign.finishedAt
    });

    if (campaign.status === 'pending' || campaign.status === 'in_progress') {
      pollTimer.current = setTimeout(() => handlePollStatus(id), 2000);
    }
  };

  const handleTestSend = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = buildPayload();
      if (!payload.message) {
        setError('Введите текст сообщения.');
        return;
      }
      const result = await API.notifications.testSend(payload);
      if (!result.success) {
        handleApiError(result.error);
        return;
      }
      setCampaignId(null);
      setProgress({ status: 'test_sent', totalRecipients: 0, sentCount: 0, failedCount: 0, startedAt: null, finishedAt: null });
    } catch (e) {
      handleApiError(e?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = buildPayload();
      if (!payload.message) {
        setError('Введите текст сообщения.');
        return;
      }
      const result = await API.notifications.start(payload);
      if (!result.success) {
        handleApiError(result.error);
        return;
      }
      const id = result.data?.id;
      setCampaignId(id);
      setProgress({ status: 'pending', totalRecipients: result.data.totalRecipients || 0, sentCount: 0, failedCount: 0, startedAt: null, finishedAt: null });
      if (onCampaignCreated) {
        onCampaignCreated(result.data);
      }
      handlePollStatus(id);
    } catch (e) {
      handleApiError(e?.message);
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = progress.totalRecipients > 0
    ? Math.round(((progress.sentCount + progress.failedCount) / progress.totalRecipients) * 100)
    : 0;

  return (
    <div className="notification-panel">
      <div className="notification-panel__header">
        <div>
          <h2>Массовая рассылка Telegram</h2>
          <p>Отправляйте текст, фото или видео всем пользователям бота. Перед запуском можно отправить тестовую копию на жестко заданный Telegram ID.</p>
        </div>
      </div>

      <div className="notification-panel__grid">
        <label className="notification-panel__field">
          <span>Название кампании (для истории)</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например, Новое обновление" />
        </label>

        <label className="notification-panel__field full-width">
          <span>Текст сообщения</span>
          <textarea
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Введите сообщение для пользователей..."
          />
        </label>

        <div className="notification-panel__field">
          <span>Формат Telegram</span>
          <select value={parseMode} onChange={(e) => setParseMode(e.target.value)}>
            <option value="Markdown">Markdown</option>
            <option value="MarkdownV2">MarkdownV2</option>
            <option value="HTML">HTML</option>
          </select>
        </div>

        <div className="notification-panel__field">
          <span>Тип контента</span>
          <select value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
            <option value="text">Только текст</option>
            <option value="photo">Фото</option>
            <option value="video">Видео</option>
          </select>
        </div>

        {mediaType !== 'text' && (
          <label className="notification-panel__field full-width">
            <span>URL медиа</span>
            <input value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="https://example.com/image.jpg" />
          </label>
        )}

        <div className="notification-panel__buttons-section full-width">
          <div className="notification-panel__buttons-header">
            <span>Кнопки (Inline)</span>
            <button type="button" className="notification-panel__secondary-button" onClick={addButton} disabled={buttons.length >= 3}>
              Добавить кнопку
            </button>
          </div>

          {buttons.map((button, index) => (
            <div className="notification-panel__button-row" key={index}>
              <input
                value={button.text}
                onChange={(e) => updateButton(index, 'text', e.target.value)}
                placeholder="Текст кнопки"
              />
              <input
                value={button.url}
                onChange={(e) => updateButton(index, 'url', e.target.value)}
                placeholder="URL кнопки"
              />
              <button type="button" className="notification-panel__remove-button" onClick={() => removeButton(index)}>
                Удалить
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="notification-panel__footer">
        <div className="notification-panel__actions">
          <button type="button" className="notification-panel__button" onClick={handleTestSend} disabled={loading || !message.trim()}>
            Отправить тест
          </button>
          <button type="button" className="notification-panel__button notification-panel__button--primary" onClick={handleStart} disabled={loading || !message.trim()}>
            Запустить рассылку
          </button>
        </div>

        <div className="notification-panel__meta">
          <div>Тест отправляется на жестко заданный Telegram ID</div>
          {campaignId && <div>Кампания #{campaignId}</div>}
        </div>
      </div>

      <div className="notification-panel__status">
        <div className="notification-panel__status-row">
          <strong>Статус:</strong> {progress.status || 'не запущена'}
        </div>
        {progress.totalRecipients > 0 && (
          <>
            <div className="notification-panel__status-row">
              <strong>Получателей:</strong> {progress.totalRecipients}
            </div>
            <div className="notification-panel__status-row">
              <strong>Отправлено:</strong> {progress.sentCount}
            </div>
            <div className="notification-panel__status-row">
              <strong>Ошибок:</strong> {progress.failedCount}
            </div>
            <div className="notification-panel__progress-bar">
              <div className="notification-panel__progress-fill" style={{ width: `${progressPercentage}%` }} />
            </div>
          </>
        )}
        {error && <div className="notification-panel__error">{error}</div>}
      </div>
    </div>
  );
};

export default NotificationPanel;
